import { css } from '@emotion/react';
import {
  CellMouseOutEvent,
  CellMouseOverEvent,
  ColDef,
  GridApi,
  GridReadyEvent,
  GetDataPath,
  GetRowIdParams,
  ICellRendererParams,
  IsFullWidthRowParams,
  RowClassParams,
  RowClickedEvent,
} from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgTableWithBaseStyle } from '../../../../components/AgTable/AgTable2';
import { agForesightCompactTheme } from '../../../../theme/ag-foresight';
import { buildClassPaths, IEquityLot, IEquityTotals, sortLotsByClass } from './equity-holdings-model';
import { fundsByIdMapAtom } from '../../../../services/state/AppConfigStateJ';
import { getBandSummary, getRestructureColor } from './security-history-model';
import { getSecurityClass } from './security-class-model';
import {
  buildChainStory,
  getCapitalThreads,
  getConversionLinks,
  IRoundTerms,
  getInPlaceRestructures,
  getLinkedRows,
  transactionsOfLot,
} from './security-story-model';
import { StoryBanner } from './StoryBanner';
import {
  getSecurityHistoryAutoGroupColDef,
  getSecurityHistoryColDefs,
  OwnershipAggFunc,
  ownershipAggFunc,
} from './securityHistoryColDefs';
import { MetricsTransactionDataModel } from '../../../../schemas/MetricsTransaction.schema';
import { DistinctFundsAggFunc, distinctFundsAggFunc } from './securityOverviewColDefs';

const gridContainer = css`
  padding: 0 0.5rem 0.5rem;

  /* Expanding is a whole-row target, as the prototype has it. */
  .ag-row.equity-row-expandable {
    cursor: pointer;
  }

  /* Everything a hovered row is directly tied to washes in one light mint, the same hue every time —
     the identity colour lives on the chips. Deliberately nowhere near purple or indigo, which the app
     spends on selection and interactivity. */
  .ag-row.equity-row-paired,
  .ag-row.equity-row-paired.ag-row-hover {
    background-color: rgba(106, 180, 172, 0.28);
  }

  /* The selection, darker and carrying an accent bar. */
  .ag-row.equity-row-story,
  .ag-row.equity-row-story.ag-row-hover {
    background-color: rgba(40, 46, 154, 0.14);
    box-shadow: inset 3px 0 0 var(--mui-palette-primary-main);
  }

  /* The tint is the hover feedback for a linked pair. AG paints its own hover as a :before overlay
     in the accent colour, which lands on the hovered end only — splitting one restructure into two
     colours. The same goes for the selected story row. */
  .ag-row.equity-row-paired.ag-row-hover::before,
  .ag-row.equity-row-story.ag-row-hover::before {
    content: none;
  }
`;

/**
 * The band row: the design spans the full table width for a group, rather than repeating column
 * values, with the group name on the left and a one-line summary on the right.
 */
const bandRow = css`
  align-items: center;
  background: var(--mui-palette-action-hover);
  border-top: 1px solid var(--mui-palette-divider);
  display: flex;
  gap: 0.75rem;
  height: 100%;
  justify-content: space-between;
  padding: 0 0.75rem;
`;

const bandName = css`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const bandSummary = css`
  color: var(--mui-palette-text-secondary);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

function BandRow({ node }: ICellRendererParams<IEquityLot>) {
  const lots = (node.allLeafChildren ?? [])
    .map((leaf) => leaf.data)
    .filter((data): data is IEquityLot => data != null);

  return (
    <div css={bandRow}>
      <span css={bandName}>{String(node.key ?? '')}</span>
      <span css={bandSummary}>{getBandSummary(lots)}</span>
    </div>
  );
}

// Compact, as the sibling holdings grid on this page is: the chips and the restructure links are
// hard to follow across a tall row. The group indent is halved because the bands are full-width rows
// — the tree is only ever two levels deep inside one, and the default indent spent the width the
// security names need.
const historyGridTheme = agForesightCompactTheme.withParams({
  fontSize: 12,
  rowGroupIndentSize: '14px',
});

const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  flex: 1,
};

const aggFuncs = {
  [DistinctFundsAggFunc]: distinctFundsAggFunc,
  [OwnershipAggFunc]: ownershipAggFunc,
};

interface ISecurityHistoryGridProps {
  /** Every lot, held or not — History's subject is the whole lifecycle. */
  lots: IEquityLot[];
  /** Totals of exactly `lots`, so the pinned Total cannot drift from the KPI strip above it. */
  totals: IEquityTotals;
  /** Transactions keyed by lot, already cut to the as-of date, for the chains and the links. */
  transactionsByLot: Map<string, MetricsTransactionDataModel[]>;
  /** Round terms by round id, for the cap, post-money and QSBS clock on the story chips. */
  roundTerms: Map<number, IRoundTerms>;
}

/**
 * Security History: every lot a company has held, grouped by round class and expandable like
 * Overview, but with the lifecycle columns — proceeds, ownership and MOIC — a lifecycle chip per
 * lot, and the restructure links that tie a converted position to what it became.
 */
export function SecurityHistoryGrid({
  lots,
  totals,
  transactionsByLot,
  roundTerms,
}: ISecurityHistoryGridProps) {
  const rowData = useMemo(() => sortLotsByClass(lots), [lots]);
  const paths = useMemo(() => buildClassPaths(lots), [lots]);
  const funds = useAtomValue(fundsByIdMapAtom);

  const [storyLotId, setStoryLotId] = useState<string | null>(null);
  const [hoveredLotId, setHoveredLotId] = useState<string | null>(null);
  const apiRef = useRef<GridApi<IEquityLot> | null>(null);

  const links = useMemo(() => getConversionLinks(transactionsByLot), [transactionsByLot]);

  // The shape the data actually uses: a restructure recorded within a single lot.
  const inPlaceRestructures = useMemo(() => getInPlaceRestructures(transactionsByLot), [transactionsByLot]);

  // One capital thread, one colour — the chain and the fund slices joined transitively; see
  // `getCapitalThreads`.
  const threads = useMemo(() => getCapitalThreads(lots, links), [lots, links]);

  // The colour a lot's chips and highlight share, named by its thread.
  const threadColorOf = useCallback(
    (lotId: string) => getRestructureColor(threads.get(lotId) ?? lotId),
    [threads]
  );

  const lotsById = useMemo(
    () => lots.reduce((acc, lot) => acc.set(lot.id, lot), new Map<string, IEquityLot>()),
    [lots]
  );

  // The design's chips name the counterpart by class alone — "→ A-1", "↳ Note".
  const counterpartClass = useCallback(
    (lotId: string) => {
      const counterpart = lotsById.get(lotId);
      return counterpart === undefined ? '' : getSecurityClass(counterpart.securityClass).shortLabel;
    },
    [lotsById]
  );

  const fundName = useCallback(
    (fundId: number | null) => (fundId === null ? '' : (funds.get(fundId)?.name ?? '')),
    [funds]
  );

  const storyLot = useMemo(
    () => (storyLotId === null ? null : (lotsById.get(storyLotId) ?? null)),
    [lotsById, storyLotId]
  );

  const storyChips = useMemo(
    () =>
      storyLotId === null || storyLot === null
        ? []
        : buildChainStory({
            lotId: storyLotId,
            lotsById,
            transactions: transactionsOfLot(transactionsByLot),
            links,
            fundName,
            roundTerms,
            inPlaceRestructures,
          }),
    [storyLotId, storyLot, lotsById, transactionsByLot, links, fundName, roundTerms, inPlaceRestructures]
  );

  // A selected story keeps its links tinted; hovering another row previews that one instead.
  const highlighted = useMemo(
    () => getLinkedRows(hoveredLotId ?? storyLotId, lots, links),
    [hoveredLotId, storyLotId, lots, links]
  );

  const nameCellParams = useMemo(
    () => ({
      links,
      inPlaceRestructures,
      counterpartClass,
      threadColorOf,
      onSelectStory: setStoryLotId,
      onHoverLot: setHoveredLotId,
    }),
    [links, inPlaceRestructures, counterpartClass, threadColorOf]
  );

  const columnDefs = useMemo(() => getSecurityHistoryColDefs(), []);
  const autoGroupColumnDef = useMemo(
    () => getSecurityHistoryAutoGroupColDef(nameCellParams),
    [nameCellParams]
  );

  const getDataPath = useCallback<GetDataPath<IEquityLot>>(
    (lot) => paths.get(lot.id) ?? [lot.securityClass],
    [paths]
  );

  const getRowId = useCallback((params: GetRowIdParams<IEquityLot>) => params.data.id, []);

  // The prototype highlights a conversion on hovering anywhere in the row, not just its chip.
  const onCellMouseOver = useCallback((event: CellMouseOverEvent<IEquityLot>) => {
    setHoveredLotId(event.data?.id ?? null);
  }, []);

  const onCellMouseOut = useCallback((_event: CellMouseOutEvent<IEquityLot>) => {
    setHoveredLotId(null);
  }, []);

  const onRowClicked = useCallback((event: RowClickedEvent<IEquityLot>) => {
    const { node } = event;
    // Bands are drawn full width, so they have no chevron; collapsing one would leave no way to
    // reopen it. The design keeps them open — only block rows toggle.
    if (node.rowPinned || !node.group || node.level === 0) {
      return;
    }
    node.setExpanded(!node.expanded);
  }, []);

  // Only the top level bands. Block and fund rows keep their column values, as the design draws them.
  const isFullWidthRow = useCallback(
    (params: IsFullWidthRowParams<IEquityLot>) =>
      params.rowNode.group === true && params.rowNode.level === 0 && !params.rowNode.rowPinned,
    []
  );

  const onGridReady = useCallback((event: GridReadyEvent<IEquityLot>) => {
    apiRef.current = event.api;
  }, []);

  const getRowClass = useCallback(
    ({ node, data }: RowClassParams<IEquityLot>) => {
      if (node.rowPinned) {
        return undefined;
      }
      const classes: string[] = [];
      if (node.group && node.level > 0) {
        classes.push('equity-row-expandable');
      }
      if (data != null && highlighted.has(data.id)) {
        classes.push('equity-row-paired');
      }
      if (data != null && data.id === storyLotId) {
        classes.push('equity-row-story');
      }
      return classes.length === 0 ? undefined : classes;
    },
    [highlighted, storyLotId]
  );

  /**
   * AG Grid evaluates `getRowClass` when it draws a row, not when React state changes, so a highlight
   * would appear on the first hover and then never clear. Redrawing on change is what actually keeps
   * the tint and the selection in sync with the state driving them.
   */
  useEffect(() => {
    apiRef.current?.redrawRows();
  }, [highlighted, storyLotId]);

  const pinnedTopRowData = useMemo<IEquityLot[]>(
    () => [
      {
        id: 'total',
        restructureId: '',
        fundId: null,
        securityName: 'Total',
        securityType: '',
        securityClass: '',
        initialHolding: null,
        investmentDate: null,
        shares: totals.shares,
        invested: totals.invested,
        fmv: totals.fmv,
        proceeds: totals.proceeds,
        ownershipPct: totals.ownershipPct,
        securityOwnershipPct: null,
        moic: totals.moic,
        lifecycle: 'active',
      },
    ],
    [totals]
  );

  return (
    <>
      {storyLot !== null && (
        <StoryBanner
          chips={storyChips}
          onDismiss={() => setStoryLotId(null)}
          securityName={storyLot.securityName}
        />
      )}
      <div css={gridContainer}>
        <AgTableWithBaseStyle
          aggFuncs={aggFuncs}
          autoGroupColumnDef={autoGroupColumnDef}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout='autoHeight'
          getDataPath={getDataPath}
          fullWidthCellRenderer={BandRow}
          getRowClass={getRowClass}
          getRowId={getRowId}
          isFullWidthRow={isFullWidthRow}
          onCellMouseOut={onCellMouseOut}
          onCellMouseOver={onCellMouseOver}
          onGridReady={onGridReady}
          groupDefaultExpanded={1}
          onRowClicked={onRowClicked}
          pinnedTopRowData={pinnedTopRowData}
          rowData={rowData}
          suppressAggFuncInHeader
          theme={historyGridTheme}
          treeData
        />
      </div>
    </>
  );
}
